// TeacherWelcomeStep.js - Welcome step for teacher onboarding
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const TeacherWelcomeStep = ({ userName, onNext, onSkip }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const firstName = userName?.split(' ')[0] || t('there');

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#6366f1',
    border: isDark ? '#475569' : '#e2e8f0',
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: colors.accent + '15' }]}>
          <MaterialCommunityIcons name="human-male-board" size={48} color={colors.accent} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('Welcome to 3D AI Tutor')}
        </Text>

        {/* Personalized greeting */}
        <Text style={[styles.greeting, { color: colors.textSecondary }]}>
          {t("Hi {name}, we're excited to have you as an educator!").replace('{name}', firstName)}
        </Text>

        {/* Teacher-focused illustration */}
        <View style={[styles.illustration, { backgroundColor: colors.bg, borderColor: colors.border }]}>
          <MaterialCommunityIcons name="clipboard-text-outline" size={40} color={colors.accent} />
          <MaterialCommunityIcons name="account-group-outline" size={48} color={colors.accent} />
          <MaterialCommunityIcons name="chart-line" size={40} color={colors.accent} />
        </View>

        {/* Description */}
        <Text style={[styles.description, { color: colors.textSecondary }]}>
          {t('Empower your students with interactive 3D models, AI-powered tutoring, and detailed analytics to track their progress.')}
        </Text>

        {/* Get Started Button */}
        <TouchableOpacity
          style={[styles.primaryButton, { backgroundColor: colors.accent }]}
          onPress={onNext}
          activeOpacity={0.8}
        >
          <Text style={styles.primaryButtonText}>{t('Get Started')}</Text>
          <MaterialCommunityIcons name="arrow-right" size={20} color="#fff" />
        </TouchableOpacity>

        {/* Skip Link */}
        <TouchableOpacity onPress={onSkip} style={styles.skipButton}>
          <Text style={[styles.skipText, { color: colors.textSecondary }]}>
            {t('Skip onboarding')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  greeting: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  illustration: {
    width: '100%',
    height: 120,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    flexDirection: 'row',
    gap: 24,
  },
  description: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 32,
    paddingHorizontal: 8,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 10,
    width: '100%',
    gap: 8,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  skipButton: {
    marginTop: 16,
    padding: 8,
  },
  skipText: {
    fontSize: 14,
  },
});

export default TeacherWelcomeStep;
